import { useMemo, useState } from "react";
import {
  createSchemaScope,
  describeExpression,
  fromExpression,
  humaniseLabel,
  parseExpression,
  typeCheck,
  type DataSource,
  type FieldSchema,
  type FieldType
} from "@broadpaper/core";
import { Popover } from "../../ui/floating.js";
import { Button } from "../../ui/primitives.js";
import { Icon } from "../../ui/icons.js";
import { DataPicker, FORMAT_SUGGESTIONS } from "../DataPicker.js";
import { CalculationBuilder, canCalculate } from "./CalculationBuilder.js";

/**
 * How a binding reads in the inspector.
 *
 * `describeExpression` is the editor's one answer to "what is this expression
 * called", and it is shared with the canvas chips, the layer tree and the
 * breadcrumb precisely so that two parts of the interface cannot call the same
 * thing by two different names. This field used to describe expressions itself and
 * fell back to the raw source for anything it did not recognise, which put
 * `sum(invoice.lineItems.amount) * (1 + coalesce(invoice.taxRate, 0)) | currency:"GBP"`
 * into a 200px box beside the word "Value" — truncated, unreadable, and in a host
 * with expressions switched off, uneditable as well.
 *
 * `computed` is the other half of the answer: it says this is arithmetic over
 * fields rather than a field, which is what decides whether the panel offers the
 * calculation builder or the plain field list first.
 */
export function useExpressionLabel(dataSources: DataSource[], scopeVars: Record<string, FieldSchema> | undefined) {
  return useMemo(() => {
    const scope = createSchemaScope(dataSources).child(scopeVars ?? {});
    return (expr: string): { label: string; error: string | null; computed: boolean } => {
      if (!expr.trim()) return { label: "", error: null, computed: false };
      const error = typeCheck(expr, scope).issues[0]?.message ?? null;
      let computed = true;
      try {
        const ast = parseExpression(expr);
        computed = !(ast.kind === "path" || (ast.kind === "pipe" && ast.input.kind === "path"));
      } catch {
        // Unparseable: not a computation, just wrong. The type checker's message
        // is what the user needs, and it is already in `error`.
        computed = false;
      }
      return { label: describeExpression(expr, scope).label, error, computed };
    };
  }, [dataSources, scopeVars]);
}

/**
 * The same thing for a text field carrying `{{ tokens }}`.
 *
 * A caption reading `{{ invoice.dueDate ? "by " + (invoice.dueDate | date:"d MMMM yyyy") : "" }}`
 * is template syntax in a plain text box: a user without an expression editor can
 * edit it, but only into something broken. `humaniseLabel` turns each token into
 * the name of what it shows.
 */
export function useTokenText(dataSources: DataSource[], scopeVars: Record<string, FieldSchema> | undefined) {
  return useMemo(() => {
    const scope = createSchemaScope(dataSources).child(scopeVars ?? {});
    return (text: string) => humaniseLabel(text, scope);
  }, [dataSources, scopeVars]);
}

export interface BindingFieldProps {
  value: string;
  onChange: (expr: string) => void;
  dataSources: DataSource[];
  scopeVars?: Record<string, FieldSchema>;
  arraysOnly?: boolean;
  types?: FieldType[];
  placeholder?: string;
  allowExpression?: boolean;
  /**
   * Whether arithmetic is a sensible answer for this property.
   *
   * False for the few props that are an expression by nature rather than a value
   * — a chart's category label, a table rule's condition. Offering to total
   *  something up there would be offering a wrong answer confidently.
   */
  allowCalculation?: boolean;
}

/**
 * A button that opens the value panel; shows the human label of the current
 * binding.
 *
 * The panel has two halves and the binding decides which opens first: a field, or
 * a calculation. Both are available to every host — a formula built by choosing
 * steps is not a Cloud concession, it is the designer finally being able to author
 * the arithmetic its own templates were shipping.
 */
export function BindingField({ value, onChange, dataSources, scopeVars, arraysOnly, types, placeholder, allowExpression = true, allowCalculation = true }: BindingFieldProps) {
  const describe = useExpressionLabel(dataSources, scopeVars);
  const { label, error, computed } = describe(value);

  /*
   * Whether the builder can take this value on.
   *
   * `fromExpression` refuses anything it would re-emit differently, so a value it
   * declines is shown and left alone rather than offered up to a panel that would
   * quietly rewrite it. A list binding is never a calculation.
   */
  const editable = allowCalculation && !arraysOnly && canCalculate(types) && (value.trim() === "" || fromExpression(value) !== null);

  /*
   * A computed value with no editor for it, in a host with expressions off.
   *
   * This is now the narrow case it should always have been: arithmetic the builder
   * cannot represent — a conditional, a function it does not model — where the
   * only honest thing is to name what it reads and leave it. The picker's one
   * offer would be a plain field, and taking it would replace a document's
   * arithmetic with one of its inputs in a single click.
   */
  if (value && computed && !allowExpression && !editable) {
    return (
      <div className="bpe-binding">
        <span className="bpe-binding__btn bpe-binding__btn--locked" title={`Worked out for you, from ${label.replace(/^ƒ\s*/, "")}. Change those and this changes with them.`}>
          <Icon name="sigma" />
          <span className="bpe-truncate">{label.replace(/^ƒ\s*/, "") || "Worked out for you"}</span>
        </span>
      </div>
    );
  }

  return (
    <div className="bpe-binding">
      <Popover
        // Wider when the calculation builder is reachable, and one width for both
        // tabs: a popover that resizes when you change tab reads as a glitch.
        className={`bpe-picker${editable ? " bpe-picker--wide" : ""}`}
        trigger={
          <button
            type="button"
            className={`bpe-binding__btn ${value ? "" : "bpe-binding__btn--empty"} ${error ? "bpe-input--invalid" : ""}`}
            // The raw expression is a developer's tooltip, so it is only offered
            // where there is a developer: a host with expressions switched off
            // gets the error, or the readable label, and never the source.
            title={error ?? (allowExpression ? value : label)}
          >
            <Icon name={error ? "alert-triangle" : computed ? "sigma" : arraysOnly ? "list-tree" : "braces"} style={error ? { color: "var(--bpe-danger)" } : undefined} />
            <span className="bpe-truncate">{value ? label.replace(/^ƒ\s*/, "") : placeholder ?? (arraysOnly ? "Choose a list" : "Choose a field")}</span>
          </button>
        }
      >
        {(close) => (
          <ValuePanel
            value={value}
            onChange={onChange}
            dataSources={dataSources}
            scopeVars={scopeVars}
            arraysOnly={arraysOnly}
            types={types}
            allowExpression={allowExpression}
            offerCalculation={editable}
            startOnCalculation={computed && editable}
            close={close}
          />
        )}
      </Popover>
      {value ? <Button size="sm" variant="ghost" icon="x" tooltip="Clear" onClick={() => onChange("")} /> : null}
    </div>
  );
}

/**
 * A field, or a value worked out. Two tabs, because they are two answers to one
 * question and a single screen holding both would be a screen nobody reads.
 */
function ValuePanel({
  value,
  onChange,
  dataSources,
  scopeVars,
  arraysOnly,
  types,
  allowExpression,
  offerCalculation,
  startOnCalculation,
  close
}: BindingFieldProps & { offerCalculation: boolean; startOnCalculation: boolean; close: () => void }) {
  const [tab, setTab] = useState<"field" | "calc">(startOnCalculation ? "calc" : "field");
  const formats = useMemo(() => {
    const wanted = types?.length ? types : (["currency", "number", "percent"] as FieldType[]);
    const seen = new Set<string>();
    return wanted
      .flatMap((t) => FORMAT_SUGGESTIONS[t] ?? [])
      .filter((f) => !seen.has(f.pipe) && seen.add(f.pipe));
  }, [types]);

  if (!offerCalculation) {
    return (
      <DataPicker
        dataSources={dataSources}
        scopeVars={scopeVars}
        arraysOnly={arraysOnly}
        types={types}
        value={value}
        allowExpression={allowExpression}
        onPick={(expr) => {
          onChange(expr);
          close();
        }}
        onClear={() => {
          onChange("");
          close();
        }}
      />
    );
  }

  return (
    <div>
      <div className="bpe-picker__tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === "field"} className="bpe-tab" onClick={() => setTab("field")}>
          A field
        </button>
        <button type="button" role="tab" aria-selected={tab === "calc"} className="bpe-tab" onClick={() => setTab("calc")}>
          Worked out
        </button>
      </div>
      {tab === "field" ? (
        <DataPicker
          dataSources={dataSources}
          scopeVars={scopeVars}
          types={types}
          value={value}
          allowExpression={allowExpression}
          onPick={(expr) => {
            onChange(expr);
            close();
          }}
          onClear={() => {
            onChange("");
            close();
          }}
        />
      ) : (
        <CalculationBuilder value={value} onChange={onChange} dataSources={dataSources} scopeVars={scopeVars} formats={formats} />
      )}
    </div>
  );
}
